import { NgClass } from '@angular/common'
import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, OnInit, Output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { AuthService } from '@auth/services/auth.service'
import { ScheduleValidator } from '@micetribe/forms/validators/schedule.validator'
import { Schedule } from '@personas/interfaces/persona-options.interface'
import { ClipboardService } from 'ngx-clipboard'
import { PersonaFieldTypes } from '../../enums/persona-fields-types.enum'
import { BARCODE_COLORS } from '../../models/persona.model'
import { ConfirmationPreviewComponent } from '../confirmation-preview/confirmation-preview.component'
import { ReminderScheduleComponent } from '../reminder-schedule/reminder-schedule.component'
import { SmsIntegrationComponent } from '../sms-integration/sms-integration.component'
import { WhatsappIntegrationComponent } from '../whatsapp-integration/whatsapp-integration.component'
import { ChannelSwitchComponent } from './channel-switch.component'

/** One communication tab under Confirmations & Reminders. */
type CommunicationChannelId = 'email' | 'whatsapp' | 'sms'

type CommunicationChannelTab = {
  id: CommunicationChannelId
  label: string
  visible: boolean
}

type MessageVariable = {
  title: string
  data?: { value: string }
}

/**
 * Confirmations & Reminders section of the persona form.
 * Email, WhatsApp, and reminder settings stay on the persona form and are saved with it.
 */
@Component({
  selector: 'app-confirmations-reminders',
  templateUrl: './confirmations-reminders.component.html',
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    ConfirmationPreviewComponent,
    ReminderScheduleComponent,
    ModalComponent,
    ChannelSwitchComponent,
    SmsIntegrationComponent,
    WhatsappIntegrationComponent
  ]
})
export class ConfirmationsRemindersComponent implements OnInit {
  /** Persona form. Channel flags and email fields are saved from here. */
  @Input() form: UntypedFormGroup

  /** Event used by reminder schedule validation and the confirmation preview. */
  @Input() event: any

  /** Clears when communication is turned off. */
  @Input() requireApproval: UntypedFormControl

  /** Success message shown in the confirmation preview. */
  @Input() message: UntypedFormControl

  /** Regret message shown when approval is required. */
  @Input() regret: UntypedFormControl

  /** Parent saves the persona without opening the share step, so the preview uses the latest message. */
  @Output() previewConfirmation = new EventEmitter<void>()

  /** One id per persona form so two open editors do not share a dialog. */
  readonly remindersModalId = `persona-reminders-${crypto.randomUUID()}`
  readonly barcodeModalId = `persona-barcode-color-${crypto.randomUUID()}`

  readonly isVariablesMenuOpen = signal(false)

  reminderStatus: UntypedFormControl
  messageVariablesList: MessageVariable[]
  formSchedules: UntypedFormArray
  reminderPrice: { amount: number; currency: string }
  colorList: { name: string; value: string }[] = BARCODE_COLORS
  barcodeColor: string
  maxCharLength = 250

  /** The reminders dialog keeps at most this many drafts. */
  readonly maxReminders = 3

  /** Shared classes for every channel tab. Active and hidden states are added beside this. */
  readonly channelTabClass =
    'tw-mb-[-1px] tw-w-auto tw-items-center tw-gap-2 tw-whitespace-nowrap tw-border-x-0 tw-border-t-0 tw-border-b-2 tw-border-solid tw-bg-transparent tw-px-1 tw-pb-2 tw-text-sm'

  /** Shared classes for the icon buttons beside the email preview. */
  readonly iconButtonClass =
    'tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded tw-border tw-border-gray-300 tw-bg-white tw-text-[#1F2A37] dark:tw-border-gray-600 dark:tw-bg-gray-800 dark:tw-text-gray-200'

  /** Tabs in order. SMS stays hidden until that channel has settings. */
  readonly channels: CommunicationChannelTab[] = [
    { id: 'email', label: 'Email', visible: true },
    { id: 'whatsapp', label: 'WhatsApp', visible: true },
    { id: 'sms', label: 'SMS', visible: true }
  ]

  /** Channel shown under Confirmations & Reminders. Email keeps the existing editor. */
  communicationChannel: CommunicationChannelId = 'email'

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _modalService = inject(ModalService)
  private readonly _auth = inject(AuthService)
  private readonly _clipboard = inject(ClipboardService)
  private readonly _alert = inject(AlertService)

  private readonly _defaultPendingApprovalMessage =
    'Thank you for submitting your request. Your application is under review.<br /><br />You will receive your confirmation once it is approved.'

  /**
   * Options group on the persona form. Reminders and the barcode color live here.
   */
  private get _options(): UntypedFormGroup {
    return this.form.controls['options'] as UntypedFormGroup
  }

  /**
   * Reminder settings inside the options group.
   */
  private get _reminders(): UntypedFormGroup {
    return this._options.controls['reminders'] as UntypedFormGroup
  }

  /**
   * Builds reminder drafts and the personalize-message menu from the persona already on the form.
   * Drops both dialog registrations when this editor is destroyed.
   */
  public ngOnInit(): void {
    this._destroyRef.onDestroy(() => {
      this._modalService.unregister(this.remindersModalId)
      this._modalService.unregister(this.barcodeModalId)
    })
    this._initPendingApprovalMessage()
    this._getMessageCustomizations()
    this.closeReminder()
    this.barcodeColor = this._options.controls['barcodeColor']?.value
    this._fetchReminderPrice()
  }

  /**
   * Copies the saved reminder schedules into a draft the dialog can edit without writing the persona until Save.
   */
  private _initFormSchedule(): void {
    this.formSchedules = new UntypedFormArray([])
    const schedulesData = this.form.value?.options?.reminders?.schedules
    if (schedulesData?.length) schedulesData.forEach((data: Schedule) => this.addReminder(data))
    else this.addReminder({ type: 'hour', value: 8, done: false })
  }

  /**
   * Loads the reminder price for the workspace plan so the schedule dialog can show it.
   */
  private _fetchReminderPrice(): void {
    const planId = this._auth.currentWorkspace.plan_id
    this._auth
      .getPlansWithPrices()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((prices) => {
        const price = prices.find((o) => o.id === planId)
        this.reminderPrice = {
          amount: price?.reminderPrice?.unit_amount,
          currency: price?.reminderPrice?.currency
        }
      })
  }

  /**
   * Closes the variable list when the click lands outside it.
   */
  @HostListener('document:click')
  public closeVariablesMenu(): void {
    this.isVariablesMenuOpen.set(false)
  }

  /**
   * Copies one placeholder so it can be pasted into the confirmation editor.
   */
  public copyMessageVariable(value?: string): void {
    if (!value) return
    this._clipboard.copy(value)
    this._alert.alert(Alert.INFO, 'Variable copied. Paste it in the confirmation editor to personalize your confirmations.')
    this.isVariablesMenuOpen.set(false)
  }

  /**
   * Adds a reminder draft. The dialog allows at most {@link maxReminders}.
   */
  public addReminder(data?: Schedule): void {
    if (this.formSchedules.length >= this.maxReminders) return
    const newReminder = new UntypedFormGroup(
      {
        type: new UntypedFormControl(data ? data.type : '', [Validators.required]),
        value: new UntypedFormControl(data ? data.value : '', [Validators.required]),
        done: new UntypedFormControl(data ? data.done : false)
      },
      [ScheduleValidator.validateWith(this.formSchedules.controls, this.event)]
    )
    this.formSchedules.push(newReminder)
  }

  /** Drops one draft. Saving writes whatever remains, including an empty list. */
  public removeReminder(index: number): void {
    this.formSchedules.removeAt(index)
  }

  /**
   * Writes the reminder drafts back onto the persona form.
   */
  private _saveReminder(): void {
    const schedules = this._reminders.controls['schedules'] as UntypedFormArray
    this._reminders.controls['enabled']?.patchValue(this.reminderStatus.value)
    schedules.clear()
    this.formSchedules.controls.forEach((schedule) => schedules.push(schedule))
  }

  /**
   * Copies the saved reminder switch and schedules into drafts the dialog can edit.
   * Save writes them back. Closing without Save calls this again and drops the edits.
   * The dialog X already toggled itself and only calls this.
   */
  public closeReminder(): void {
    this.reminderStatus = new UntypedFormControl(this._reminders.controls['enabled']?.value)
    this._initFormSchedule()
  }

  public openBarcodeColorDialog(): void {
    this._openModal(this.barcodeModalId)
  }

  /**
   * Puts the last saved color back. Used when the dialog closes without Save.
   */
  public restoreBarcodeColor(): void {
    this.barcodeColor = this.form.value.options.barcodeColor
  }

  /**
   * Remembers the color until Save writes it onto the persona.
   */
  public selectBarcodeColor(color: string): void {
    this.barcodeColor = color
  }

  /** Writes the chosen color onto the persona and closes the dialog. */
  public saveBarcodeAndClose(): void {
    this._options.controls['barcodeColor'].patchValue(this.barcodeColor)
    this._closeModal(this.barcodeModalId)
  }

  public isBarcodeModalOpen(): boolean {
    return this._modalService.isModalOpen(this.barcodeModalId)
  }

  /**
   * Builds the personalize menu from the persona's checkbox and radio fields.
   */
  private _getMessageCustomizations(): void {
    const formElements: [] = this.form.value.form_elements
    const messageVariables: MessageVariable[] = []
    messageVariables.push({ title: 'Full name - {{fullname}}', data: { value: '{{fullname}}' } })
    messageVariables.push({ title: 'First name - {{firstname}}', data: { value: '{{firstname}}' } })
    formElements.forEach((el: any) => {
      if (el.form_element_type_id == PersonaFieldTypes.CHECKBOX || el.form_element_type_id == PersonaFieldTypes.RADIO) {
        messageVariables.push({ title: `${el.label} - {{${el.name}}}`, data: { value: `{{${el.name}}}` } })
      }
    })
    this.messageVariablesList = messageVariables
  }

  public openRemindersDialog(): void {
    this._openModal(this.remindersModalId)
  }

  /**
   * Discards unsaved drafts, then closes. The modal X already toggled itself and only calls closeReminder.
   */
  public dismissReminders(): void {
    this.closeReminder()
    this._closeModal(this.remindersModalId)
  }

  /**
   * Writes reminder drafts onto the persona, then closes the dialog.
   */
  public saveReminders(): void {
    this._saveReminder()
    this.closeReminder()
    this._closeModal(this.remindersModalId)
  }

  public isRemindersModalOpen(): boolean {
    return this._modalService.isModalOpen(this.remindersModalId)
  }

  /**
   * Turning communication off also clears pre-approval, which only applies to emailed registrants.
   */
  public toggleMailCommunication(event: Event): void {
    const enableCommunication = (event.target as HTMLInputElement).checked
    if (!enableCommunication) this.requireApproval.patchValue(false, { emitEvent: false })
  }

  /**
   * Shows Email, WhatsApp, or SMS settings under Confirmations & Reminders.
   */
  public selectCommunicationChannel(channel: CommunicationChannelId): void {
    this.communicationChannel = channel
  }

  public get pendingApprovalMessage(): UntypedFormControl {
    return this.form.controls['options'].get('pendingApprovalMessage') as UntypedFormControl
  }

  /**
   * Opens a dialog unless it is already open. Toggle flips, so a second call would close it.
   */
  private _openModal(id: string): void {
    if (!this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }

  /**
   * Closes a dialog unless it is already closed. The dialog X toggles itself before Cancel or Save runs.
   */
  private _closeModal(id: string): void {
    if (this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }

  /**
   * Adds the pending-approval message when an older persona was saved without one.
   */
  private _initPendingApprovalMessage(): void {
    const options = this.form.controls['options'] as UntypedFormGroup
    if (!options.get('pendingApprovalMessage')) {
      options.addControl('pendingApprovalMessage', new UntypedFormControl(this._defaultPendingApprovalMessage))
    }
  }
}
